// 피격 상태 연출(c012, COMBAT-FEEL 2.7): 다운·기상·다운 추격을 VFX 모듈 라이브러리로 그린다(새 드로우콜 없음 — 모듈당 1회를 같이 쓴다).
// - Ev.MobDown: 몹이 땅에 눕는 순간 발밑 흙먼지 고리 + 작은 방사 균열(균열은 스킬 균열을 밀어내지 않게 빈 칸이 넉넉할 때만) + 먼지 입자.
// - Ev.MobWake: 기상 무적(600ms) 동안 몸을 감싸는 옅은 청백 오라(막힌 타격의 강철색과 같은 '피해 없음' 언어). 무적 길이만큼만 보인다.
//   오라 상한(틱 4·동시 8)을 넘으면 발밑 강철색 고리 표식으로 대신한다(c015). 표식도 틱 2개·고리 빈 칸 2 이상일 때만(스킬 고리 보호), 넘으면 wakeSkipped로 센다.
// - Ev.Hit + HitFlag.DownChase: 누운 몸 높이의 낮은 잿불 불똥(바닥을 기는 부채) + 아주 작은 바닥 고리. 일반 접촉 불똥(허리 높이 흰 별)과 구별된다.
// 혼전에서 한 판정 순간(시뮬레이션 틱)에 여러 마리가 동시에 눕거나 일어나므로 틱마다 연출 수에 상한을 둔다(다운은 먼지 입자까지 같은 상한, c015).
// 피·혈흔은 쓰지 않는다(기본 12+). 프레임 루프·이벤트 처리 안에서 새 객체를 만들지 않는다(대상 핸들·프리셋은 생성 때 고정).
import { Ev, HitFlag, type GameEvent } from '../shared/events';
import { MobState, type MobsView } from '../shared/types';
import type { ParticleFx } from './particles';
import { makeTarget, type FxPreset, type FxTarget, type VfxModules } from './modules';
import { MODULE_CAPS } from './modules/types';
import { AURA_RELEASE } from './modules/aura';

const EMBER = 0xff6a2a;
const WARM_WHITE = 0xfff1d6;
/** 흙먼지(가산 혼합이라 어둡게: 밝은 돌바닥에서 하얗게 뜨지 않는다) */
const DUST = 0xc8784a;
/** 기상 무적 오라: 차가운 강철 청색(방패 막힘 STEEL과 같은 '안 먹힘' 언어). c013: 거의 흰색(0x9ab8ec·0xe8f0ff, 세기 1.3)이 블룸에서 하얗게 떴다 → 채도 낮은 강철 청으로 낮춤 */
const STEEL = 0x5f80b8;
const STEEL_WHITE = 0xaac4ec;

/** 한 판정 순간(틱)에 띄우는 연출 상한. 기상은 오라 상한이고, 넘거나 오라 모듈이 가득이면 싼 고리 표식으로 대신한다(무적 표시는 버리지 않는다, c015) */
export const HIT_STATE_PER_TICK = { down: 3, wake: 4, chase: 3, marker: 2 } as const;
/**
 * 기상 대체 고리 표식은 고리 모듈에 빈 칸이 이만큼 이상 있을 때만(스킬 착지 고리·다운 고리를 밀어내지 않는다, PR #157 리뷰 P1).
 * 표식도 못 띄운 기상은 stats.wakeSkipped로 센다(대혼전에서 고리 모듈을 기상 표식이 다 차지하지 않게)
 */
export const WAKE_MARKER_RING_FREE_MIN = 2;
/** 다운 균열은 균열 모듈에 빈 칸이 이만큼 이상 있을 때만(스킬 착지·초승달 균열을 밀어내지 않는다) */
export const DOWN_CRACK_FREE_MIN = 2;

export const HIT_STATE_PRESETS = {
  // 2026-10-22 c012 캡처: 흙색(0x8a6a4a)·반경 1.1m는 가산 혼합이라 밝은 돌바닥에서 보이지 않았다 → 따뜻한 잿불 흙빛·1.4m
  downRing: { length: 1.4, from: 0.3, width: 0.4, life: 0.4, color: DUST, color2: WARM_WHITE, intensity: 1.35, seed: 0.3 },
  /** 체공 상한 강제 다운(b = 1)은 조금 더 크게 */
  downRingForced: { length: 1.8, from: 0.25, width: 0.45, life: 0.42, color: EMBER, color2: WARM_WHITE, intensity: 1.4, seed: 0.6 },
  downCrack: { variant: 1, length: 1.3, life: 0.75, color: EMBER, color2: WARM_WHITE, intensity: 1.3, seed: 0.85 },
  wakeAura: { y: 0.0, length: 1.7, width: 0.62, life: 0.6, color: STEEL, color2: STEEL_WHITE, intensity: 1.05, seed: 0.2 },
  chaseRing: { length: 0.85, from: 0.2, width: 0.22, life: 0.26, color: EMBER, color2: WARM_WHITE, intensity: 1.6, seed: 0.45 },
  /** 기상 무적 대체 표식(c015): 오라 상한을 넘은 기상 몹 발밑의 얇은 강철색 고리(고리 모듈 1슬롯, 새 드로우콜 없음). life는 남은 무적 시간 */
  wakeMarker: { length: 0.95, from: 0.75, width: 0.16, life: 0.6, color: STEEL, color2: STEEL_WHITE, intensity: 1.0, seed: 0.15 },
} as const satisfies Record<string, FxPreset>;

/** 낮은 불똥 색(HDR, 잿불 주황) */
const CHASE_SPARK = [2.6, 0.95, 0.28] as const;
/**
 * 오라 대상 핸들 수: 오라 모듈 상한의 2배. 핸들을 돌려 쓸 때 그 핸들을 따르던 이전 오라는 이미 모듈에서 밀려났다
 * (다른 몹 핸들을 다시 써도 옛 오라가 새 몹으로 따라가 겹치지 않는다)
 */
const WAKE_TARGETS = MODULE_CAPS.aura * 2;
/** 기상 오라 모듈 수명 안전 상한(초, 영웅 시계). 실제 끝은 세계 시계·몹 상태로 대상을 놓아 정한다 */
export const WAKE_AURA_CAP = 6;

export class HitStateFx {
  /** QA 집계: 받은 이벤트와 실제로 띄운 연출 */
  readonly stats = { down: 0, downFx: 0, downCrack: 0, wake: 0, wakeFx: 0, wakeMarker: 0, wakeSkipped: 0, chase: 0, chaseFx: 0 };
  private budgetTick = -1;
  private bDown = 0; private bWake = 0; private bChase = 0; private bMarker = 0;
  private scale = 1;
  private readonly targets: FxTarget[] = [];
  private nextTarget = 0;
  /** 오라 대상이 기상한 몹 인덱스(같은 몹이 다시 일어나면 그 핸들을 다시 쓴다) */
  private readonly targetMob = new Int32Array(WAKE_TARGETS).fill(-1);
  /** 오라를 끝낼 시각(초, 이 객체의 시계) */
  private readonly targetEnd = new Float64Array(WAKE_TARGETS);
  /** 기상한 몹의 세대(슬롯 재사용 판별). -1 = 첫 update에서 읽는다 */
  private readonly targetGen = new Int32Array(WAKE_TARGETS).fill(-1);
  private t = 0;
  private readonly wakePreset: FxPreset = { ...HIT_STATE_PRESETS.wakeAura };
  private readonly markerPreset: FxPreset = { ...HIT_STATE_PRESETS.wakeMarker };

  constructor(private readonly mods: VfxModules, private readonly particles: ParticleFx) {
    for (let i = 0; i < WAKE_TARGETS; i++) { const t = makeTarget(); t.active = false; this.targets.push(t); }
  }

  /** 품질 배율(0..1): 입자 수·틱당 상한에 곱한다. 0이면 새 연출을 내지 않는다 */
  setScale(s: number): void { this.scale = Math.max(0, Math.min(1, Number.isFinite(s) ? s : 1)); this.budgetTick = -1; }

  private budget(tick: number): void {
    if (tick === this.budgetTick) return;
    this.budgetTick = tick;
    const k = this.scale;
    this.bDown = Math.round(HIT_STATE_PER_TICK.down * k);
    this.bWake = Math.round(HIT_STATE_PER_TICK.wake * k);
    this.bChase = Math.round(HIT_STATE_PER_TICK.chase * k);
    this.bMarker = Math.round(HIT_STATE_PER_TICK.marker * k);
  }

  /**
   * 렌더가 Ev.MobDown·Ev.MobWake·Ev.Hit를 넘긴다. lagSec: 이벤트가 늦게 처리된 만큼(세계 시계 초 — 한 프레임에 여러 틱이 지난 따라잡기 프레임).
   * 기상 오라는 남은 무적 시간만큼만 보인다. 반환: 모듈을 띄웠으면 true
   */
  onEvent(e: GameEvent, lagSec = 0): boolean {
    if (this.scale <= 0) return false;
    switch (e.type) {
      case Ev.MobDown: return this.down(e);
      case Ev.MobWake: return this.wake(e, lagSec);
      case Ev.Hit:
        if (e.b !== 2 && (e.flags & HitFlag.DownChase) !== 0) return this.chase(e);
        return false;
      default: return false;
    }
  }

  private down(e: GameEvent): boolean {
    this.stats.down++;
    this.budget(e.tick);
    // c015(PR #155 리뷰 이월 9번): 먼지 입자도 같은 틱 예산 안에서만 — 혼전에서 한 틱에 수십 마리가 누워도 입자 생성이 상한을 넘지 않는다
    if (this.bDown <= 0) return false;
    this.bDown--;
    this.particles.dust(e.x, e.z, Math.max(1, Math.round(4 * this.scale)));
    this.stats.downFx++;
    const forced = e.b === 1;
    this.mods.play('ring', forced ? HIT_STATE_PRESETS.downRingForced : HIT_STATE_PRESETS.downRing, e.x, 0, e.z, 0);
    const crack = this.mods.crack;
    if (crack.capacity - crack.activeCount >= DOWN_CRACK_FREE_MIN) {
      this.mods.play('crack', HIT_STATE_PRESETS.downCrack, e.x, 0, e.z, (e.a * 1.7) % 6.283);
      this.stats.downCrack++;
    }
    return true;
  }

  private wake(e: GameEvent, lagSec: number): boolean {
    this.stats.wake++;
    this.budget(e.tick);
    const full = e.b > 0 ? e.b / 60 : HIT_STATE_PRESETS.wakeAura.life;
    // Codex P2(c013): 따라잡기 프레임에서 늦게 온 만큼 무적이 이미 지났다 — 남은 시간만 보이고, 이미 끝났으면 띄우지 않는다
    const lag = Math.max(0, Number.isFinite(lagSec) ? lagSec : 0);
    const remaining = full - lag;
    if (remaining <= 1 / 120) return false;
    // 같은 몹의 핸들이 살아 있으면 다시 쓰고, 아니면 다음 핸들을 쓴다
    let slot = -1;
    for (let i = 0; i < WAKE_TARGETS; i++) if (this.targetMob[i] === e.a) { slot = i; break; }
    // 같은 몹의 오라가 아직 떠 있으면 끝 시각만 늘린다(같은 핸들에 오라 두 겹 금지, 예산 안 씀)
    if (slot >= 0 && this.targets[slot].active) { this.targetEnd[slot] = Math.max(this.targetEnd[slot], this.t + remaining); return true; }
    // c015(PR #155 리뷰 이월 10번): 틱 예산을 넘었거나 오라 모듈이 가득이면(살아 있는 무적 오라를 밀어내지 않게) 싼 고리 표식으로 대신한다.
    // 무적 표시를 조용히 버리지 않는다. 고리는 몹을 따라가지 않고 발밑에 남은 무적 시간만큼 보인다
    const aura = this.mods.aura;
    if (this.bWake <= 0 || aura.activeCount >= aura.capacity) return this.marker(e, remaining);
    if (slot < 0) {
      // 비어 있는 핸들만 쓴다(살아 있는 다른 몹의 오라 핸들을 빼앗아 그 무적 표시를 옮기지 않게)
      for (let k = 0; k < WAKE_TARGETS && slot < 0; k++) {
        const i = (this.nextTarget + k) % WAKE_TARGETS;
        if (!this.targets[i].active) slot = i;
      }
      if (slot < 0) return this.marker(e, remaining);
      this.nextTarget = (slot + 1) % WAKE_TARGETS;
    }
    this.bWake--;
    const tgt = this.targets[slot];
    this.targetGen[slot] = -1;
    tgt.x = e.x; tgt.y = 0; tgt.z = e.z; tgt.yaw = 0; tgt.active = true;
    // 늦게 온 만큼 나타나는 구간(0.15초)을 건너뛴다.
    // 끝은 이 객체가 세계 시계(기상 무적이 세는 시계)와 몹 상태로 정해 대상을 놓는다(놓으면 0.2초에 사라진다). 오라 모듈은 영웅 시계로 늙으므로
    // 모듈 수명은 안전 상한(WAKE_AURA_CAP)만 둔다 — 각성처럼 세계가 느려져도(0.2배) 무적이 남은 동안 먼저 꺼지지 않게(Codex P2 c013)
    const a0 = Math.min(lag, 0.15);
    this.wakePreset.age0 = a0;
    this.wakePreset.life = a0 + Math.max(WAKE_AURA_CAP, remaining + AURA_RELEASE);
    this.targetMob[slot] = e.a;
    this.targetEnd[slot] = this.t + remaining;
    this.mods.playAura(this.wakePreset, tgt);
    this.stats.wakeFx++;
    return true;
  }

  /** 기상 무적 대체 표식: 발밑 얇은 강철색 고리를 남은 무적 시간만큼(고리 모듈, 새 드로우콜 없음) */
  private marker(e: GameEvent, remaining: number): boolean {
    // 틱 예산(2)과 고리 모듈 빈 칸(2 이상)을 지킨다: 넘으면 띄우지 않고 센다(스킬 고리 밀어내기 금지)
    const ring = this.mods.ring;
    if (this.bMarker <= 0 || ring.capacity - ring.activeCount < WAKE_MARKER_RING_FREE_MIN) { this.stats.wakeSkipped++; return false; }
    this.bMarker--;
    const m = this.markerPreset;
    m.life = Math.max(0.2, remaining);
    this.mods.play('ring', m, e.x, 0, e.z, 0);
    this.stats.wakeMarker++;
    return true;
  }

  private chase(e: GameEvent): boolean {
    this.stats.chase++;
    this.budget(e.tick);
    // 누운 몸 높이(0.25m)에서 휘두른 방향으로 바닥을 기는 낮은 부채(위로 거의 안 튄다)
    const n = Math.max(2, Math.round((this.bChase > 0 ? 10 : 4) * this.scale));
    this.particles.streaks(e.x, 0.25, e.z, e.d, 1.5, n, 6, CHASE_SPARK[0], CHASE_SPARK[1], CHASE_SPARK[2], 0.2, 0.22, 0.6);
    if (this.bChase <= 0) return false;
    this.bChase--;
    this.mods.play('ring', HIT_STATE_PRESETS.chaseRing, e.x, 0, e.z, 0);
    this.stats.chaseFx++;
    return true;
  }

  /**
   * 세계 시계 초 단위 진행(기상 무적은 세계 틱으로 센다): 끝난 기상 오라의 대상을 놓는다(오라가 0.2초 안에 사라진다).
   * mobs가 있으면 살아 있는 오라 대상을 몹 인덱스로 매 프레임 몹 위치(alpha 보간)로 옮기고, 몹이 기상 상태를 벗어나거나(죽음·재사용) 세대가 바뀌면 놓는다.
   * Codex P2(c013): 대상 위치를 기상 순간 한 번만 복사해 각성 끌어당김 등으로 움직인 기상 몹을 오라가 따라가지 않았다.
   */
  update(dtSec: number, mobs?: MobsView, alpha = 1): void {
    this.t += dtSec;
    for (let i = 0; i < WAKE_TARGETS; i++) {
      const idx = this.targetMob[i];
      if (idx < 0) continue;
      const tgt = this.targets[i];
      if (mobs !== undefined && idx < mobs.capacity) {
        if (this.targetGen[i] < 0) this.targetGen[i] = mobs.gen[idx];
        if (mobs.state[idx] !== MobState.Wakeup || mobs.gen[idx] !== this.targetGen[i]) { tgt.active = false; this.targetMob[i] = -1; continue; }
        const a = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
        tgt.x = mobs.prevX[idx] + (mobs.x[idx] - mobs.prevX[idx]) * a;
        tgt.z = mobs.prevZ[idx] + (mobs.z[idx] - mobs.prevZ[idx]) * a;
      }
      if (this.t >= this.targetEnd[i]) {
        tgt.active = false;
        this.targetMob[i] = -1;
      }
    }
  }

  clear(): void {
    for (let i = 0; i < WAKE_TARGETS; i++) { this.targets[i].active = false; this.targetMob[i] = -1; }
    this.budgetTick = -1;
  }
}
